import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import './Accounts.scss';
import { Loader } from '../../../components/Loader/Loader';
import { FaAngleLeft, FaAngleRight, FaFlag } from 'react-icons/fa';
import Input from '../../../components/inputs/Input';
import { Account } from '../../../types/userTypes';
import { fetchAccounts } from 'fetch/fetchUsers';
import { formatPhoneNumber } from '../../../utils/formatting';
import { UsersMenu } from '../UsersMenu';
import { EditAccountModal } from './EditAccountModal';
import { capitalize } from '../../../utils/strings';
import { toHumanReadable } from '../../../utils/country';
import { ERROR } from 'const/error';

const PAGE_SIZE = 25;
const SEARCH_WAIT_INTERVAL = 300;

function RenderPaginationHeader({
  loading,
  searchText,
  page,
  total,
  items,
  onSearchChange,
  onChangePage,
}: {
  loading: boolean;
  searchText: string;
  page: number;
  total: number;
  items: Account[];
  onSearchChange: (searchText: string) => void;
  onChangePage: (offset: number) => void;
}) {
  const firstItemIndex = page * PAGE_SIZE;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  return (
    <>
      <div className="account--list__header">
        <Input
          className="account-list__header-search"
          placeholder="Search text..."
          onChange={event => onSearchChange(event.target.value)}
          value={searchText}
          autoFocus={searchText !== ''}
          onRef={input => searchText !== '' && input && input.focus()}
          disabled={loading}
        />

        <div className="account--list__pagination-header">
          {!loading && total > 0 && (
            <span className="account--list__pagination-info">
              {firstItemIndex + 1}-{firstItemIndex + items.length} of {total}
            </span>
          )}

          <span className="account--list__pagination-nav">
            <span
              className="account--list__pagination-navbut"
              onClick={() => onChangePage(-1)}
            >
              <FaAngleLeft
                style={{
                  width: 30,
                  height: 30,
                  color: loading || page <= 0 ? 'rgba(0,0,0,0.26)' : '#3a3d4b',
                }}
              />
            </span>
            <span
              className="account--list__pagination-navbut"
              onClick={() => onChangePage(1)}
            >
              <FaAngleRight
                style={{
                  width: 30,
                  height: 30,
                  color:
                    loading || page >= totalPages - 1
                      ? 'rgba(0,0,0,0.26)'
                      : '#3a3d4b',
                }}
              />
            </span>
          </span>
        </div>
      </div>
    </>
  );
}

function AccountsTable({
  accounts,
  loading,
  onAccountSelected,
}: {
  accounts: Account[];
  loading: boolean;
  onAccountSelected: (account: Account) => void;
}) {
  return (
    <>
      <table className="account-list__table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Name</th>
            <th>Org. number</th>
            <th>Email</th>
            <th>Phone</th>
            <th>
              <FaFlag />
            </th>
          </tr>
        </thead>
        <tbody>
          {!loading &&
            accounts.map((account: Account) => (
              <tr key={account.id} onClick={() => onAccountSelected(account)}>
                <td>{capitalize(account.category)}</td>
                <td>{account.name}</td>
                <td>{account.companyNumber}</td>
                <td>{account.email}</td>
                <td>
                  {formatPhoneNumber(account.phoneCode + account.phoneNumber)}{' '}
                </td>
                <td>{toHumanReadable(account.countryCode)}</td>
              </tr>
            ))}
        </tbody>
      </table>

      {loading && (
        <div>
          <Loader type="bounce" size="xxl" />
        </div>
      )}
      {!loading && accounts.length === 0 && (
        <div className="account-list__table-message">
          {ERROR.ACCOUNTS_NOT_FOUNDS}
        </div>
      )}
    </>
  );
}

export default function Accounts(): React.ReactElement | null {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [page, setPage] = useState<number>(0);
  const [inputSearchText, setInputSearchText] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  let typingTimer: any = null;

  const loadAccounts = async (): Promise<Account[]> => {
    try {
      return (await fetchAccounts())
        .filter(a => a.enabled)
        .sort(
          (a1, a2) =>
            new Date(a2.updated).getTime() - new Date(a1.updated).getTime()
        );
    } catch (e) {
      console.log('Error fetching accounts', e);
      return [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setAccounts(await loadAccounts());
      setIsLoading(false);
      setPage(0);
    };
    fetchData();
  }, []);

  useEffect(() => {
    return () => clearTimeout(typingTimer);
  }, [typingTimer]);

  const reloadData = async () => {
    setIsLoading(true);
    const accounts = await loadAccounts();
    setAccounts(accounts);
    setIsLoading(false);
    setPage(0);
  };

  const handleSearchChange = (searchText: string) => {
    clearTimeout(typingTimer);
    setInputSearchText(searchText);
    typingTimer = setTimeout(() => {
      setPage(0);
      setSearchText(searchText);
    }, SEARCH_WAIT_INTERVAL);
  };

  const changePage = (offset: number) => {
    if (isLoading) return;
    const totalPages = Math.ceil(accounts.length / PAGE_SIZE);
    const nextPage = page + offset;

    if (nextPage < 0 || nextPage >= totalPages) {
      return;
    }
    setPage(nextPage);
  };

  const accountSelected = (account: Account) => {
    setSelectedAccount(account);
    setShowEditModal(true);
  };

  const text = searchText.toLowerCase().trim();
  const filteredAcc =
    text === ''
      ? accounts
      : accounts.filter(
          a =>
            a.name.toLowerCase().includes(text) ||
            a.companyNumber.toLowerCase().includes(text) ||
            a.email.toLowerCase().includes(text) ||
            (a.phoneCode + a.phoneNumber).includes(text)
        );
  const idx = page * PAGE_SIZE;
  const accPage = filteredAcc.slice(idx, idx + PAGE_SIZE);

  return (
    <>
      <Helmet>
        <title>Users - Accounts</title>
      </Helmet>
      <UsersMenu />
      <section className="account-list">
        <h3>Accounts</h3>

        <div className="account--list__container">
          <RenderPaginationHeader
            loading={isLoading}
            searchText={inputSearchText}
            page={page}
            total={filteredAcc.length}
            items={accPage}
            onSearchChange={handleSearchChange}
            onChangePage={changePage}
          />

          <AccountsTable
            onAccountSelected={accountSelected}
            loading={isLoading}
            accounts={accPage}
          />
        </div>
        <div>
          {showEditModal && (
            <>
              {selectedAccount && (
                <div onClick={() => setShowEditModal(false)}>
                  <EditAccountModal
                    account={selectedAccount}
                    onCloseModal={updated => {
                      setShowEditModal(false);
                      if (updated) {
                        reloadData();
                      }
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
